import { useState, useEffect } from 'react';
import SEO, { PAGE_SEO } from '../components/SEO';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Skeleton } from '../components/ui/skeleton';
import { Brain, Sparkles, CheckCircle, XCircle, ArrowRight, RefreshCw, Trophy } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Quiz() {
  const { user, updateUser } = useAuth();
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [quizComplete, setQuizComplete] = useState(false);
  const [score, setScore] = useState({ correct: 0, total: 0, points: 0 });

  const token = localStorage.getItem('skinuse_token');
  const headers = { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) };

  useEffect(() => {
    loadQuestions();
  }, []);

  const loadQuestions = async () => {
    setLoading(true);
    setQuizComplete(false);
    setCurrentIndex(0);
    setScore({ correct: 0, total: 0, points: 0 });
    setResult(null);
    setSelectedAnswer(null);

    try {
      const res = await fetch(`${BACKEND_URL}/api/quiz/questions?limit=5`);
      const data = await res.json();
      setQuestions(data);
    } catch (error) {
      console.error('Quiz load error:', error);
      toast.error('Failed to load quiz questions');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (selectedAnswer === null || !questions[currentIndex]) return;
    setSubmitting(true);

    try {
      const res = await fetch(`${BACKEND_URL}/api/quiz/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...headers
        },
        body: JSON.stringify({
          question_id: questions[currentIndex].question_id,
          selected_index: selectedAnswer
        })
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to submit answer');
      }

      const data = await res.json();
      setResult(data);

      // Update score
      setScore(prev => ({
        correct: prev.correct + (data.correct ? 1 : 0),
        total: prev.total + 1,
        points: prev.points + (data.points_earned || 0)
      }));

      if (data.correct && data.points_earned > 0) {
        updateUser({ skin_points: (user?.skin_points || 0) + data.points_earned });
      }
    } catch (error) {
      console.error('Submit error:', error);
      toast.error(error.message || 'Failed to submit answer');
    } finally {
      setSubmitting(false);
    }
  };

  const handleNext = () => {
    if (currentIndex >= questions.length - 1) {
      setQuizComplete(true);
    } else {
      setCurrentIndex(prev => prev + 1);
      setSelectedAnswer(null);
      setResult(null);
    }
  };

  const currentQuestion = questions[currentIndex];
  const progress = ((currentIndex + (result ? 1 : 0)) / questions.length) * 100;

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <main className="py-8 px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mx-auto">
            <Skeleton className="h-8 w-48 mb-4" />
            <Skeleton className="h-4 w-64 mb-8" />
            <Card className="border-none">
              <CardContent className="p-8">
                <Skeleton className="h-2 w-full mb-8" />
                <Skeleton className="h-8 w-full mb-6" />
                <div className="space-y-3">
                  {[...Array(4)].map((_, i) => (
                    <Skeleton key={i} className="h-16 w-full" />
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background" data-testid="quiz-page">
      <SEO {...PAGE_SEO.quiz} url="/quiz" />
      <Navbar />
      
      <main className="flex-1 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-gold/10 rounded-sm mb-4">
              <Brain className="w-4 h-4 text-gold" />
              <span className="text-xs font-semibold uppercase tracking-wider text-gold">Quiz & Trivia</span>
            </div>
            <h1 className="font-serif text-3xl sm:text-4xl font-semibold mb-2">Test Your Knowledge</h1>
            <p className="text-muted-foreground">Earn SkinPoints by answering correctly!</p>
          </div>

          {quizComplete ? (
            /* Quiz Complete Screen */
            <Card className="bg-card border-none shadow-float" data-testid="quiz-complete">
              <CardContent className="p-12 text-center">
                <div className="w-20 h-20 rounded-full bg-gold/20 flex items-center justify-center mx-auto mb-6">
                  <Trophy className="w-10 h-10 text-gold" />
                </div>
                <h2 className="font-serif text-3xl font-semibold mb-2">Quiz Complete!</h2>
                <p className="text-muted-foreground mb-8">Great job testing your skincare knowledge</p>
                
                <div className="grid grid-cols-3 gap-6 mb-8">
                  <div className="p-4 bg-secondary rounded-sm">
                    <p className="font-serif text-3xl font-semibold">{score.correct}</p>
                    <p className="text-xs text-muted-foreground">Correct</p>
                  </div>
                  <div className="p-4 bg-secondary rounded-sm">
                    <p className="font-serif text-3xl font-semibold">{score.total}</p>
                    <p className="text-xs text-muted-foreground">Total</p>
                  </div>
                  <div className="p-4 bg-gold/10 rounded-sm">
                    <p className="font-serif text-3xl font-semibold text-gold">+{score.points}</p>
                    <p className="text-xs text-muted-foreground">Points</p>
                  </div>
                </div>

                <div className="flex gap-4 justify-center">
                  <Button onClick={loadQuestions} className="btn-primary gap-2" data-testid="play-again">
                    <RefreshCw className="w-4 h-4" />
                    Play Again
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : questions.length === 0 ? (
            <Card className="border-none">
              <CardContent className="p-12 text-center">
                <p className="text-muted-foreground">No quiz questions available yet.</p>
              </CardContent>
            </Card>
          ) : (
            /* Quiz Question */
            <Card className="bg-card border-none shadow-float" data-testid="quiz-question">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between text-sm text-muted-foreground mb-4">
                  <span>Question {currentIndex + 1} of {questions.length}</span>
                  <span className="flex items-center gap-1">
                    <Sparkles className="w-4 h-4 text-gold" />
                    {currentQuestion?.points || 20} pts
                  </span>
                </div>
                <Progress value={progress} className="h-1" />
              </CardHeader>
              <CardContent className="p-8 pt-4">
                <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-gold">
                  {currentQuestion?.category}
                </div>
                <h2 className="font-serif text-xl sm:text-2xl font-semibold mb-8">
                  {currentQuestion?.question}
                </h2>

                <div className="space-y-3 mb-8">
                  {currentQuestion?.options.map((option, index) => {
                    let buttonClass = 'w-full p-4 text-left border rounded-sm transition-all ';
                    
                    if (result) {
                      if (index === result.correct_index) {
                        buttonClass += 'border-green-500 bg-green-50 dark:bg-green-950/20';
                      } else if (index === selectedAnswer && !result.correct) {
                        buttonClass += 'border-red-500 bg-red-50 dark:bg-red-950/20';
                      } else {
                        buttonClass += 'border-border opacity-50';
                      }
                    } else if (selectedAnswer === index) {
                      buttonClass += 'border-gold bg-gold/10';
                    } else {
                      buttonClass += 'border-border hover:border-gold/50 hover:bg-gold/5';
                    }

                    return (
                      <button
                        key={index}
                        onClick={() => !result && setSelectedAnswer(index)}
                        disabled={!!result}
                        className={buttonClass}
                        data-testid={`option-${index}`}
                      >
                        <div className="flex items-center gap-3">
                          <span className="w-8 h-8 rounded-sm bg-secondary flex items-center justify-center text-sm font-semibold">
                            {String.fromCharCode(65 + index)}
                          </span>
                          <span className="flex-1">{option}</span>
                          {result && index === result.correct_index && (
                            <CheckCircle className="w-5 h-5 text-green-500" />
                          )}
                          {result && index === selectedAnswer && !result.correct && (
                            <XCircle className="w-5 h-5 text-red-500" />
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>

                {/* Result Explanation */}
                {result && (
                  <div className={`p-4 rounded-sm mb-6 ${result.correct ? 'bg-green-50 dark:bg-green-950/20' : 'bg-red-50 dark:bg-red-950/20'}`}>
                    <div className="flex items-center gap-2 mb-2">
                      {result.correct ? (
                        <>
                          <CheckCircle className="w-5 h-5 text-green-500" />
                          <span className="font-semibold text-green-700 dark:text-green-400">Correct! +{result.points_earned} points</span>
                        </>
                      ) : (
                        <>
                          <XCircle className="w-5 h-5 text-red-500" />
                          <span className="font-semibold text-red-700 dark:text-red-400">Incorrect</span>
                        </>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">{result.explanation}</p>
                  </div>
                )}

                {/* Actions */}
                <div className="flex justify-end gap-3">
                  {!result ? (
                    <Button
                      onClick={handleSubmit}
                      disabled={selectedAnswer === null || submitting}
                      className="btn-gold gap-2"
                      data-testid="submit-answer"
                    >
                      {submitting ? 'Checking...' : 'Submit Answer'}
                    </Button>
                  ) : (
                    <Button
                      onClick={handleNext}
                      className="btn-primary gap-2"
                      data-testid="next-question"
                    >
                      {currentIndex >= questions.length - 1 ? 'See Results' : 'Next Question'}
                      <ArrowRight className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
